"use strict";
/**
 ShapeDiver 3D Viewer Application

 MaterialPresets.js

 Content:
   Library of material presets and handler function

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialPresets
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MaterialPresetsInstance = void 0;
/**
 * Global utilities
 */
var GlobalUtils_1 = __importDefault(require("../util/GlobalUtils"));
var ViewerAppConstants_1 = __importDefault(require("../../app/ViewerAppConstants"));
/**
 * Location of material textures
 */
var TEXTURE_PATH = 'https://viewer.shapediver.com/v2/materials/';
var MATERIAL_PRESETS_URL = 'https://viewer.shapediver.com/v2/materials/db.json';
/**
 * Fallback database of material presets in case loading from MATERIAL_PRESETS_URL fails
 */
var MaterialPresetsDbFallback_1 = __importDefault(require("./MaterialPresetsDbFallback"));
var HttpClient_1 = require("../../modules/http-client/HttpClient");
/**
 * Default resolution, available resolutions
 */
var RESOLUTIONS = ['256', '512', '1024', '2048'];
var DEFAULTRESOLUTION = '1024'; // #SS-866 make it configurable
/**
 * @classdesc Material preset container singleton
 * @class
 * @hideconstructor
 */
var MaterialPresets = /** @class */ (function () {
    // #endregion Properties (2)
    // #region Constructors (1)
    function MaterialPresets() {
        var _this = this;
        // #region Properties (2)
        this._materials = {};
        this._materialsPromise = HttpClient_1.HttpClientInstance.getCacheAsync(MATERIAL_PRESETS_URL).then(function (response) {
            var jsonMaterials = response.data;
            _this.parseMaterialDatabase(jsonMaterials);
        }, function () { return _this.parseMaterialDatabase(MaterialPresetsDbFallback_1.default); });
    }
    // #endregion Constructors (1)
    // #region Public Methods (3)
    /**
     * Return preset material by id
     * @public
     * @function
     * @param  {Integer} id The id of the preset. See table below.
     * @return {module:JSONMaterial~JSONMaterial}    JSON material of version 2.0
     */
    MaterialPresets.prototype.fromId = function (id) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                // Wrap into promise, it might happen that material fetch was not resolved.
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        // use material promise, to make sure that they're resolved.
                        _this._materialsPromise.then(function () {
                            var resolution;
                            // check if we got a resolution as part of the id
                            if (typeof id === 'string') {
                                var parts = id.split('@');
                                id = parts[0];
                                if (parts.length > 1) {
                                    resolution = parts[1];
                                }
                            }
                            if (!RESOLUTIONS.includes(resolution)) {
                                resolution = DEFAULTRESOLUTION;
                            }
                            // convert to number
                            id = Number(id);
                            // for a while, we had documented the presets to be 10, 20, 30 and 40
                            // here, we allow for the few cases where this was used to succeed
                            if (id < 100 && id % 10 == 0) {
                                id /= 10;
                            }
                            // if the id is less than 10, multiply it by 100
                            // if (Math.floor(Math.log(id) / Math.log(10)) == 0) id *= 100;
                            if (id < 10)
                                id *= 100;
                            // split id into class and specific material
                            var classID = Math.floor(id / 100);
                            var specificID = id - (classID * 100);
                            // check for material
                            if (_this.hasMaterial(classID, specificID)) {
                                resolve(_this.getMaterial(classID, specificID, resolution));
                            }
                            else if (_this.hasMaterial(classID, 0)) {
                                resolve(_this.getMaterial(classID, 0, resolution));
                            }
                            else if (_this.hasMaterial(0, 0)) {
                                resolve(_this.getMaterial(0, 0, resolution));
                            }
                            else {
                                // the material database doesn't even have a default material, use the global one
                                resolve(ViewerAppConstants_1.default.defaultSettings.defaultMaterial);
                            }
                        }, function (reason) { return reject(reason); });
                    })];
            });
        });
    };
    /** Get material */
    MaterialPresets.prototype.getMaterial = function (classId, specificId, resolution) {
        // make sure classId and specificId are given in the format we use internally
        classId = this.castId(classId);
        specificId = this.castId(specificId);
        resolution = resolution || DEFAULTRESOLUTION;
        // get material
        var mat = GlobalUtils_1.default.deepCopy(this._materials[classId][specificId].mat);
        // set texture properties according to resolution and texture path
        var basePath = TEXTURE_PATH + resolution + '/' + classId + '/' + specificId + '/';
        ['bitmap', 'metalness', 'roughness', 'bump', 'normal', 'transparency'].forEach(function (t) {
            var tt = t + 'texture';
            if (mat.hasOwnProperty(tt)) {
                mat[tt] = basePath + mat[tt];
            }
        });
        return mat;
    };
    /** Check if material exists*/
    MaterialPresets.prototype.hasMaterial = function (classId, specificId) {
        // make sure classId and specificId are given in the format we use internally
        classId = this.castId(classId);
        specificId = this.castId(specificId);
        // check library
        if (!this._materials.hasOwnProperty(classId))
            return false;
        if (!this._materials[classId].hasOwnProperty(specificId))
            return false;
        return true;
    };
    // #endregion Public Methods (3)
    // #region Private Methods (3)
    /** Helper function for adding statically defined materials*/
    MaterialPresets.prototype.addMaterial = function (classId, specificId, categoryMaterial, specificMaterial) {
        // make sure classId and specificId are given in the format we use internally
        classId = this.castId(classId);
        specificId = this.castId(specificId);
        var mat = { version: null, name: null };
        Object.keys(categoryMaterial.properties).forEach(function (property) { return mat[property] = categoryMaterial.properties[property]; });
        Object.keys(specificMaterial).forEach(function (property) { return mat[property] = specificMaterial[property]; });
        // default material version
        mat.version = mat.version || '2.0';
        // add material definition to library
        if (!this._materials.hasOwnProperty(classId)) {
            this._materials[classId] = {};
        }
        this._materials[classId][specificId] = { name: mat.name, mat: mat };
    };
    /** Helper function for casting id representation to the one we use internally */
    MaterialPresets.prototype.castId = function (id) {
        if (typeof id !== 'string')
            id = String(id);
        return id.padStart(2, '0').slice(0, 2);
    };
    /**  Parse material database and add materials found */
    MaterialPresets.prototype.parseMaterialDatabase = function (jsonMaterials) {
        var _this = this;
        var numberRegex = /^\d+$/;
        Object.keys(jsonMaterials).forEach(function (classId) {
            Object.keys(jsonMaterials[classId]).forEach(function (specificId) {
                if (numberRegex.test(classId) && numberRegex.test(specificId)) {
                    _this.addMaterial(classId, specificId, jsonMaterials[classId], jsonMaterials[classId][specificId]);
                }
            });
        });
    };
    return MaterialPresets;
}());
exports.MaterialPresetsInstance = new MaterialPresets();
